import {
  CircleAlert,
  CircleCheck,
  Clock,
  Loader2,
  MinusCircle,
  X,
} from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export type RowStatus =
  | "checking"
  | "duplicate"
  | "skipped"
  | "ready"
  | "uploading"
  | "uploaded"
  | "error";

export interface PendingRow {
  filename: string;
  status: RowStatus;
  message?: string;
}

interface Props {
  rows: PendingRow[];
  onClose: () => void;
}

export function UploadResultsPanel({ rows, onClose }: Props): React.ReactElement {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">Результаты загрузки</CardTitle>
        <Button variant="ghost" size="icon" className="size-7" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.map((row, idx) => (
          <Row key={`${row.filename}-${idx}`} row={row} />
        ))}
      </CardContent>
    </Card>
  );
}

function Row({ row }: { row: PendingRow }): React.ReactElement {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="mt-0.5 shrink-0">
        <StatusIcon status={row.status} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium" title={row.filename}>
          {row.filename}
        </div>
        {row.message && (
          <div className="text-xs text-muted-foreground">{row.message}</div>
        )}
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: RowStatus }): React.ReactElement {
  switch (status) {
    case "checking":
      return <Clock className="size-4 text-muted-foreground" />;
    case "uploading":
      return <Loader2 className="size-4 animate-spin text-muted-foreground" />;
    case "uploaded":
      return <CircleCheck className="size-4 text-green-600" />;
    case "duplicate":
    case "ready":
      return <Clock className="size-4 text-amber-500" />;
    case "skipped":
      return <MinusCircle className="size-4 text-muted-foreground" />;
    case "error":
      return <CircleAlert className="size-4 text-destructive" />;
  }
}
